import { Job, JobStatus } from "@/types/job";

export class NoProfileError extends Error {
  status = 400;
  constructor(message = "No active profile") {
    super(message);
    this.name = "NoProfileError";
  }
}

export interface FetchJobsParams {
  min_score?: number;
  status?: string; // comma-separated: "Backlog,Applied,Interviewing"
  location_type?: string; // comma-separated: "remote,hybrid,onsite"
  q?: string; // text search
  limit?: number;
  cursor?: string;
  profile_hash?: string;
}

export async function fetchJobs(params?: FetchJobsParams): Promise<Job[]> {
  const searchParams = new URLSearchParams();

  if (params?.min_score !== undefined) {
    searchParams.set("min_score", params.min_score.toString());
  }
  if (params?.status) {
    searchParams.set("status", params.status);
  }
  if (params?.location_type) {
    searchParams.set("location_type", params.location_type);
  }
  if (params?.q) {
    searchParams.set("q", params.q);
  }
  if (params?.limit !== undefined) {
    searchParams.set("limit", params.limit.toString());
  }
  if (params?.cursor) {
    searchParams.set("cursor", params.cursor);
  }
  if (params?.profile_hash) {
    searchParams.set("profile_hash", params.profile_hash);
  }

  const queryString = searchParams.toString();
  const url = queryString ? `/api/jobs?${queryString}` : "/api/jobs";
  const res = await fetch(url, { cache: "no-store" });

  // Option 2 UX: treat 400 as "no profile yet"
  if (res.status === 400) {
    let msg = "Upload a resume to get started.";
    try {
      const j = await res.json();
      if (typeof j?.detail === "string" && j.detail.trim()) msg = j.detail;
    } catch {}
    throw new NoProfileError(msg);
  }

  if (!res.ok) throw new Error(`GET /api/jobs failed: ${res.status}`);
  return res.json();
}

export async function patchJob(
    id: string,
    updates: { status?: JobStatus; notes?: string }
  ): Promise<{ statusDate?: string | null }> {
    const res = await fetch(`/api/jobs/${encodeURIComponent(id)}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        status: updates.status,
        notes: updates.notes,
        local_date: new Date().toLocaleDateString('en-CA'), // YYYY-MM-DD in user's timezone
      }),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }
  
  
  

// full job description fetch
export async function fetchJobDescription(id: string): Promise<{
    job_key: string;
    has_full_description: boolean;
    fetched_at: string;
    description_hash: string;
    description_text: string;
  }> {
    const res = await fetch(`/api/jobs/${encodeURIComponent(id)}/description`, { cache: "no-store" });
    if (!res.ok) {
      if (res.status === 404) throw new Error("No stored job description found for this role.");
      throw new Error(`GET /api/jobs/${id}/description failed: ${res.status}`);
    }
    return res.json();
  }

  // rescoring a single job
export async function rescoreJob(id: string): Promise<void> {
    const res = await fetch(`/api/jobs/${encodeURIComponent(id)}/rescore`, {
      method: "POST",
    });
    if (!res.ok) {
      let detail = `Rescore failed (${res.status})`;
      try {
        const j = await res.json();
        if (typeof j?.detail === "string" && j.detail.trim()) detail = j.detail;
      } catch {}
      throw new Error(detail);
    }
  }
    
  // rescoring multiple jobs / bulk rescore
  export async function rescoreJobs(
    scope: "stale" | "all" | "jobs",
    jobIds?: string[]
  ): Promise<void> {
    const payload =
      scope === "jobs" ? { scope, job_ids: jobIds ?? [] } : { scope };
  
    const res = await fetch("/api/rescore", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
    });
  
    if (!res.ok) throw new Error(await res.text());
  }
  
  
// fetch candidate profile
export async function fetchProfile() {
    const res = await fetch("/api/profile", { cache: "no-store" });
    if (!res.ok) throw new Error("Failed to load profile");
    return res.json();
  }



//fetch resumes

export type ATSRecommendedFix = {
  approach: string;
  steps: string[];
  preserves_look_and_feel: "fully" | "mostly" | "partially" | "no";
  visual_impact: string;
  priority: "must_fix" | "should_fix" | "nice_to_fix";
};

export type ATSIssue = {
  id: string;
  severity: "critical" | "high" | "medium" | "low";
  category: string;
  title: string;
  problem: string;
  why_it_matters: string;
  evidence: string;
  recommended_fix: ATSRecommendedFix;
};

export type ATSReport = {
  score: number;
  summary: {
    overall_assessment: string;
    parsing_risk_level: "low" | "moderate" | "high" | "severe";
    most_important_takeaway: string;
  };
  issues: ATSIssue[];
  strengths: string[];
  quick_wins: { issue_id: string; action: string }[];
  critical_fixes: { issue_id: string; action: string }[];
  ats_platform_notes: {
    general: string;
    workday_like_risk: string;
    taleo_like_risk: string;
    greenhouse_lever_like_risk: string;
  };
  final_recommendation: {
    ready_to_use: boolean;
    needs_revision_before_applying: boolean;
    reason: string;
  };
};

export type ATSCheckResult =
  | { checked: false }
  | { checked: true; score: number; checked_at: string; report: ATSReport };

export type ResumeMetaRow = {
  id: number;
  profile_hash: string;
  is_active: boolean;
  is_workspace: boolean;
  archived_at: string | null;
  resume_filename: string;
  resume_source: string;
  resume_updated_at: string;
  created_at: string;
  updated_at: string;
  ats_score?: number | null;
  ats_checked_at?: string | null;
};

export async function fetchResumes(opts?: {
  includeArchived?: boolean;
  q?: string;
}): Promise<ResumeMetaRow[]> {
  const params = new URLSearchParams();
  if (opts?.includeArchived) params.set("include_archived", "true");
  if (opts?.q) params.set("q", opts.q);
  const qs = params.toString();
  const res = await fetch(qs ? `/api/resumes?${qs}` : "/api/resumes", { cache: "no-store" });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

async function throwIfNotOk(res: Response, fallback: string): Promise<void> {
  if (!res.ok) {
    const ct = res.headers.get("content-type") ?? "";
    const text = await res.text();
    throw new Error(ct.includes("html") ? `${fallback} (${res.status})` : text);
  }
}

export async function activateResume(profileId: number): Promise<void> {
  const res = await fetch(`/api/resumes/${profileId}/activate`, { method: "POST" });
  await throwIfNotOk(res, "Failed to activate resume");
}

export async function setResumeWorkspace(
  profileId: number,
  inWorkspace: boolean
): Promise<void> {
  const res = await fetch(`/api/resumes/${profileId}/workspace`, {
    method: inWorkspace ? "POST" : "DELETE",
  });
  await throwIfNotOk(res, "Failed to update workspace");
}

export async function archiveResume(profileId: number): Promise<void> {
  const res = await fetch(`/api/resumes/${profileId}/archive`, { method: "POST" });
  await throwIfNotOk(res, "Failed to archive resume");
}

export async function unarchiveResume(profileId: number): Promise<void> {
  const res = await fetch(`/api/resumes/${profileId}/unarchive`, { method: "POST" });
  await throwIfNotOk(res, "Failed to restore resume");
}

export async function renameResume(profileId: number, name: string): Promise<void> {
  const res = await fetch(`/api/resumes/${profileId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
  if (!res.ok) throw new Error(await res.text());
}

export async function getResumeATSReport(profileId: number): Promise<ATSCheckResult> {
  const res = await fetch(`/api/resumes/${profileId}/ats-check`, { cache: "no-store" });
  if (!res.ok) throw new Error(await res.text());
  const data = await res.json();
  if (!data.checked) return { checked: false };
  return { checked: true, score: data.score, checked_at: data.checked_at ?? "", report: data.report };
}

export async function runResumeATSCheck(profileId: number): Promise<{ score: number; report: ATSReport }> {
  const res = await fetch(`/api/resumes/${profileId}/ats-check`, { method: "POST" });
  if (!res.ok) throw new Error(await res.text());
  const data = await res.json();
  return { score: data.score, report: data.report };
}

export type ATSIssueComparison = {
  fixed_issues: ATSIssue[];
  remaining_issues: ATSIssue[];
  new_issues_added: ATSIssue[];
};

export type ATSRecheckResult = {
  ok: boolean;
  revision_id: number;
  original_score: number;
  new_score: number;
  improvement_pct: number;
  new_report: ATSReport;
  comparison: ATSIssueComparison;
};

export async function recheckResumeATS(
  profileId: number,
  file: File
): Promise<ATSRecheckResult> {
  const form = new FormData();
  form.append("resume", file);
  const res = await fetch(`/api/resumes/${profileId}/recheck`, {
    method: "POST",
    body: form,
  });
  if (!res.ok) {
    let detail = `Re-check failed (${res.status})`;
    try {
      const j = await res.json();
      if (typeof j?.detail === "string" && j.detail.trim()) detail = j.detail;
    } catch {}
    throw new Error(detail);
  }
  return res.json();
}

export type ATSRevisionSummary = {
  id: number;
  ats_score: number;
  created_at: string;
};

export async function listATSRevisions(profileId: number): Promise<ATSRevisionSummary[]> {
  const res = await fetch(`/api/resumes/${profileId}/revisions`, { cache: "no-store" });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// User preferences
export type SortField = "scoredOn" | "score" | "statusDate" | "title" | "company";
export type SortDirection = "asc" | "desc";

export interface UserPreferences {
  min_score: number;
  status_filters: string[];
  location_filters: string[];
  region_filters: string[];
  sort_field: SortField;
  sort_direction: SortDirection;
  status_date_window: number | null;
  row_highlights_enabled: boolean;
  preferred_regions: string[] | null;
}

export async function fetchPreferences(): Promise<UserPreferences> {
  const res = await fetch('/api/preferences', { cache: 'no-store' });
  if (!res.ok) throw new Error('Failed to load preferences');
  return res.json();
}

export async function savePreferences(prefs: UserPreferences): Promise<UserPreferences> {
  const res = await fetch('/api/preferences', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(prefs)
  });
  if (!res.ok) throw new Error('Failed to save preferences');
  return res.json();
}

// Billing

export interface BillingState {
  plan_id: string;
  plan_status: string;
  subscription_status: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  is_pro: boolean;
  is_founding_member: boolean;
  billing_enabled: boolean;
}

export async function fetchBillingState(): Promise<BillingState> {
  const res = await fetch('/api/billing/me', { cache: 'no-store' });
  if (!res.ok) throw new Error('Failed to load billing state');
  return res.json();
}

export async function createCheckoutSession(planId: string): Promise<{ url: string }> {
  const res = await fetch('/api/billing/checkout', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ plan_id: planId }),
  });
  if (!res.ok) {
    let detail = `Checkout failed (${res.status})`;
    try {
      const j = await res.json();
      if (typeof j?.detail === 'string' && j.detail.trim()) detail = j.detail;
    } catch {}
    throw new Error(detail);
  }
  return res.json();
}

export async function createBillingPortalSession(): Promise<{ url: string }> {
  const res = await fetch('/api/billing/portal', { method: 'POST' });
  if (!res.ok) {
    let detail = `Billing portal failed (${res.status})`;
    try {
      const j = await res.json();
      if (typeof j?.detail === 'string' && j.detail.trim()) detail = j.detail;
    } catch {}
    throw new Error(detail);
  }
  return res.json();
}
